import React from 'react';
import {
  IonInput,
  IonButton,
  IonIcon,
  IonAvatar,
  IonSpinner
} from '@ionic/react';
import { sendOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { AudioPlayback } from './AudioPlayback';
import { RecordingComponent } from './RecordingComponent';

import userAvatar from "../../assets/user.png";
import olha from "../../assets/Teacher11.png";
import anton from "../../assets/Teacher1.png";

export interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface ChatInterfaceProps {
  messages: Message[];
  isLoading: boolean;
  teacherId: string;
  onSendMessage: (content: string) => Promise<void>;
  contentRef: React.RefObject<HTMLDivElement>;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  isLoading,
  teacherId,
  onSendMessage,
  contentRef
}) => {
  const { t } = useTranslation();
  const [inputMessage, setInputMessage] = React.useState('');

  const handleSend = () => {
    if (inputMessage.trim()) {
      onSendMessage(inputMessage);
      setInputMessage('');
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto space-y-6 pb-4">
        {messages.map((message, index) => (
          <div key={index} className={`flex items-start gap-3 px-4 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'} animate-fadeIn`}>
            <IonAvatar className="w-10 h-10 flex-shrink-0">
              <img 
                src={message.role === 'user' ? userAvatar : (teacherId === 'anton' ? anton : olha)} 
                alt={message.role === 'user' ? 'User' : 'Tutor'} 
                className="w-full h-full object-cover rounded-full border-2 border-[#31D526]" 
              />
            </IonAvatar>
            <div className={`flex flex-col max-w-[75%] ${message.role === 'user' ? 'items-end' : 'items-start'}`}>
              <div className={`rounded-2xl px-6 py-3 shadow-lg ${
                message.role === 'user' 
                  ? 'bg-[#31D526] text-black' 
                  : 'bg-white text-black border-2 border-[#31D526]'
              }`}>
                <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
              </div>
              {message.role === 'assistant' && (
                <AudioPlayback 
                  content={message.content}
                  messageIndex={index}
                  teacherId={teacherId}
                />
              )}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <IonSpinner name="dots" className="text-[#31D526] w-12 h-12" />
          </div>
        )}
        <div ref={contentRef} />
      </div>

      <div className="sticky bottom-[30px] bg-black border-t border-[#31D526]/30">
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-[#1C1C1E] rounded-full px-6 py-3 border border-[#31D526]/30">
            <IonInput
              value={inputMessage}
              placeholder={t('russianTutor.inputPlaceholder')}
              onIonChange={(e) => setInputMessage(e.detail.value!)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              className="flex-1 text-white text-base"
              style={{ '--placeholder-color': '#6B7280', '--color': 'white' }}
            />
          </div>
          <IonButton
            onClick={handleSend}
            disabled={isLoading}
            className="h-12 w-12 rounded-full bg-[#31D526] transition-colors duration-300 shadow-lg m-0"
          >
            <IonIcon icon={sendOutline} className="w-6 h-6" />
          </IonButton>
          <RecordingComponent
            onTranscriptionComplete={onSendMessage}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};